/**
 * Magic Link Verification Page
 * Handles the magic link click from email
 */

import { useCallback, useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSession } from '@/features/auth/hooks/useSession'
import { env } from '@/lib/env'
import { getAuthItem, setAuthItem, setRefreshTokenSecure } from '@/lib/authStorage'
import { diag } from '@/lib/diag'
import { isNative } from '@/lib/capacitor'
import { ConnectionErrorScreen } from '@/common/components/ConnectionErrorScreen'
import { XaviorLoader } from '@/common/components/XaviorLoader'
import { useShouldPauseForBanner, onContinueInBrowser } from '@/common/components/OpenInAppBanner'

const API_BASE_URL = env.apiBaseUrl
const AUTH_BASE_URL = env.authBaseUrl

function safeParseJson(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

function extractErrorMessage(data: unknown, rawText?: string): string | null {
  if (typeof data === 'string' && data.trim()) return data.trim()
  if (data && typeof data === 'object') {
    const record = data as Record<string, unknown>
    if (typeof record.error === 'string') return record.error
    if (typeof record.message === 'string') return record.message
    if (typeof record.error === 'object' && record.error) {
      const nested = record.error as Record<string, unknown>
      if (typeof nested.message === 'string') return nested.message
    }
  }
  // Fall back to raw response text — Go services return plain-text errors.
  if (rawText && rawText.trim()) return rawText.trim()
  return null
}

function formatAuthError(status: number, data: unknown, rawText?: string): string {
  if (status === 429) {
    return 'Too many attempts. Please wait a moment and try again.'
  }
  if (status === 502 || status === 503 || status === 504) {
    return 'Login service is temporarily unavailable. Please try again.'
  }
  return extractErrorMessage(data, rawText) || 'Failed to verify magic link'
}

function formatNetworkError(error: unknown): string {
  if (error instanceof TypeError) {
    return 'Network error while verifying your magic link. Please check your connection and try again.'
  }
  if (error instanceof Error && error.message) {
    return error.message
  }
  return 'Failed to verify magic link'
}

function shouldShowConnectionScreen(status?: number, error?: unknown): boolean {
  if (typeof status === 'number' && status >= 500) {
    return true
  }
  if (error instanceof TypeError) {
    return true
  }
  if (error instanceof Error) {
    const message = error.message.toLowerCase()
    return (
      message.includes('failed to fetch') ||
      message.includes('network') ||
      message.includes('timed out') ||
      message.includes('timeout')
    )
  }
  return false
}

export function MagicLinkVerify() {
  const navigate = useNavigate()
  const { setToken, waitForProfile } = useSession()
  const [status, setStatus] = useState<'verifying' | 'success' | 'error' | 'waiting'>('verifying')
  const [error, setError] = useState('')
  const hasVerified = useRef(false)
  const [connectionError, setConnectionError] = useState<Error | null>(null)
  const [isReturningUser, setIsReturningUser] = useState(false)

  // Check if we should pause for the "Open in App" banner
  const shouldPauseForBanner = useShouldPauseForBanner()

  const verifyToken = useCallback(async (force?: boolean) => {
    // Prevent double verification in React StrictMode
    if (hasVerified.current && !force) return
    hasVerified.current = true
    setStatus('verifying')
    setError('')
    setConnectionError(null)

      const params = new URLSearchParams(window.location.search)
      const token = params.get('token')
      const email = params.get('email')

      diag('verify', 'start', { native: isNative(), force: Boolean(force), hasToken: Boolean(token) })
      if (!token) {
        diag('verify', 'no token in URL → error')
        setStatus('error')
        setError('No verification token found in URL')
        return
      }

      try {
        // Include email in both header (for supervisor routing) and body (for workspace)
        const headers: Record<string, string> = { 'Content-Type': 'application/json' }
        if (email) {
          headers['X-Auth-Email'] = email
        }
        // Capacitor iOS: tag the verify so the backend mints a header-refresh
        // session (returns a rotated refreshToken in the body to seed below).
        // sprint native-oauth-pass Ch.1.
        if (isNative()) {
          headers['X-Client-Platform'] = 'capacitor'
        }
        const verifyUrl = `${API_BASE_URL}/auth/verify`
        const fetchOptions: RequestInit = {
          method: 'POST',
          headers,
          body: JSON.stringify({ token, email }),
          credentials: 'include',
        }
        diag('verify', 'calling /auth/verify', { email, native: isNative() })
        const response = await fetch(verifyUrl, fetchOptions)
        console.log('[MagicLinkVerify] Response status:', response.status)
        diag('verify', `response ${response.status}`, { ok: response.ok })

        const responseText = await response.text()
        const responseData = responseText ? safeParseJson(responseText) : null

        if (!response.ok) {
          const errorMessage = formatAuthError(response.status, responseData, responseText)
          if (errorMessage.includes('already used')) {
            console.log('[MagicLinkVerify] Token already used')
            const existingToken = getAuthItem('xavior_auth_token')
            // #5105 H1 probe: "already used" + existing session === a double-fire on
            // the single-use token (the loser shows the Failed card while the winner
            // authed). The hasExistingSession flag distinguishes H1 from a genuinely
            // consumed/expired link.
            diag('verify', 'token already used', { hasExistingSession: Boolean(existingToken) })
            if (existingToken) {
              // Session already exists — this was a duplicate React StrictMode call.
              console.log('[MagicLinkVerify] Found existing session, treating as success')
              setStatus('success')
              // Post-auth → app root (PDR 010); the gate chain decides what shows.
              setTimeout(() => navigate('/'), 800)
            } else {
              console.log('[MagicLinkVerify] No session found, magic link expired or used elsewhere')
              setStatus('error')
              setError('This magic link has already been used. Please request a new one.')
            }
            return
          }
          if (shouldShowConnectionScreen(response.status)) {
            diag('verify', `connection-error screen (status ${response.status})`)
            const detail = extractErrorMessage(responseData, responseText) || response.statusText || errorMessage
            setConnectionError(new Error(`HTTP ${response.status}: ${detail}`))
            setStatus('error')
            return
          }
          diag('verify', `not ok (status ${response.status}) → throw`)
          throw new Error(errorMessage)
        }

        if (!responseData || typeof responseData !== 'object') {
          // bensig's "unexpected response from login service" lands here — an OK
          // status but a non-object/empty body (#5105 Verification-Failed bug).
          diag('verify', 'unexpected response (ok but non-object body)', {
            status: response.status,
            bodyLen: responseText.length,
          })
          throw new Error('Unexpected response from login service.')
        }

        const data = responseData as {
          accessToken?: string
          sessionToken?: string
          userId: string
          email: string
          refreshToken?: string
        }

        // Prefer the JWT (`accessToken`) over the legacy hex `sessionToken`.
        // The supervisor validates JWTs statelessly against JWT_SECRET, so
        // they survive supervisor restarts. The legacy sessionToken relies
        // on supervisor's in-memory MemoryStore (session/store.go) which is
        // wiped on every restart and never shared with api-staging — so any
        // /chat request after a supervisor restart 401s with "invalid jwt:
        // token contains an invalid number of segments". Falling back to
        // sessionToken keeps the legacy path working only if the server has
        // ENABLE_LEGACY_SESSIONS=true AND supervisor's in-memory store still
        // has the token.
        const authToken = data.accessToken || data.sessionToken
        if (!authToken) {
          diag('verify', 'response missing accessToken + sessionToken → throw')
          throw new Error('Auth response missing both accessToken and sessionToken.')
        }
        diag('verify', 'verified ok', {
          native: isNative(),
          hadRefreshToken: Boolean(data.refreshToken),
          usedAccessToken: Boolean(data.accessToken),
        })

        // Fire warmup immediately with the new token — gives a head start
        // while React state updates propagate. SessionProvider will also fire
        // warmup but the supervisor handles concurrent calls fine.
        void fetch(`${AUTH_BASE_URL}/warmup`, {
          method: 'POST',
          credentials: 'include',
          headers: { Authorization: `Bearer ${authToken}` },
        }).catch(() => {})

        setAuthItem('xavior_auth_token', authToken)
        if (data.sessionToken) {
          setAuthItem('sessionToken', data.sessionToken)
        }
        setAuthItem('userId', data.userId)
        setAuthItem('userEmail', data.email)

        // Native (Capacitor iOS): seed the refresh token into the Keychain. This
        // is the FIRST place the token arrives — apiClient's later refresh reads
        // it from here, so without this seed the first session can't survive an
        // access-token expiry (AC 1). sprint native-oauth-pass Ch.1.
        if (isNative() && data.refreshToken) {
          await setRefreshTokenSecure(data.refreshToken)
        }

        setToken(authToken)

        // Wait for the SessionProvider to fetch and validate the profile before
        // navigating, or we race ahead of the loaded profile.
        console.log('[MagicLinkVerify] Waiting for profile to load...')
        diag('verify', 'waiting for profile')
        const loadedProfile = await waitForProfile()
        // #5105 H2 probe: a null profile here is the post-verify waitForProfile race
        // (token set, but the profile fetch lost) — the other Verification-Failed path.
        diag('verify', 'profile resolved', { loaded: Boolean(loadedProfile) })

        if (!loadedProfile) {
          diag('verify', 'profile load failed → error')
          setStatus('error')
          setError('Failed to load profile. Please try logging in again.')
          return
        }

        const returning = Boolean(loadedProfile.isPaidAccount || loadedProfile.trialUsed)
        setIsReturningUser(returning)
        setStatus('success')
        diag('verify', 'success → navigate /')

        // Post-auth → app root (PDR 010); the gate chain decides what shows.
        setTimeout(() => {
          navigate('/')
        }, 800)

      } catch (err: unknown) {
        console.error('Verification error:', err)
        diag('verify', 'threw', { message: err instanceof Error ? err.message : String(err) })

        if (shouldShowConnectionScreen(undefined, err)) {
          const message = formatNetworkError(err)
          setConnectionError(err instanceof Error ? err : new Error(message))
          setStatus('error')
          return
        }
        setStatus('error')
        setError(formatNetworkError(err))
      }
  }, [setToken, waitForProfile, navigate])

  // Start verification - pause if banner is showing on mobile web
  useEffect(() => {
    if (shouldPauseForBanner) {
      // On mobile web, wait for user to choose "Continue in Browser"
      // The OpenInAppBanner will handle "Open App" option
      setStatus('waiting')
      return
    }
    verifyToken()
  }, [verifyToken, shouldPauseForBanner])

  // Listen for "Continue in Browser" event from the banner
  useEffect(() => {
    if (!shouldPauseForBanner) return

    const cleanup = onContinueInBrowser(() => {
      console.log('[MagicLinkVerify] Received continue-in-browser event')
      setStatus('verifying')
      void verifyToken(true)
    })

    return cleanup
  }, [verifyToken, shouldPauseForBanner])

  if (connectionError) {
    return (
      <ConnectionErrorScreen
        error={connectionError}
        onRetry={() => {
          setConnectionError(null)
          void verifyToken(true)
        }}
      />
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-5" style={{ background: '#f8f7f5' }}>
      <div className="bg-white rounded-2xl p-12 max-w-md w-full border" style={{ borderColor: '#e8e6e2' }}>
        <div className="text-5xl font-bold text-center text-gray-900 mb-8">XAVIOR</div>

        {status === 'waiting' && (
          <div className="text-center">
            <div className="text-gray-600 text-6xl mb-4">📱</div>
            <p className="text-gray-900 font-semibold text-lg mb-2">Open in app?</p>
            <p className="text-gray-600 text-sm mb-6">Choose how to continue with your magic link</p>
          </div>
        )}

        {status === 'verifying' && (
          <div className="text-center">
            <XaviorLoader size="lg" className="mx-auto mb-4" />
            <p className="text-gray-600">Verifying your magic link...</p>
          </div>
        )}

        {status === 'success' && (
          <div className="text-center">
            <div className="text-green-600 text-6xl mb-4">✓</div>
            <p className="text-gray-900 font-semibold text-lg mb-2">
              {isReturningUser ? 'Welcome back!' : 'Welcome!'}
            </p>
            <p className="text-gray-600">Redirecting to your workspace...</p>
          </div>
        )}

        {status === 'error' && (
          <div className="text-center">
            <div className="text-amber-600 text-6xl mb-4">⚠</div>
            <p className="text-gray-900 font-semibold text-lg mb-2">Verification Failed</p>
            <p className="text-gray-600 text-sm mb-6">{error}</p>
            <button
              onClick={() => verifyToken(true)}
              className="w-full py-3.5 px-6 text-white font-medium rounded-xl mb-3 text-sm"
              style={{ background: '#0ea5e9', transition: 'background 0.15s ease' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = '#0284c7' }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = '#0ea5e9' }}
            >
              Try Again
            </button>
            <button
              onClick={() => navigate('/login')}
              className="w-full py-3.5 px-6 bg-gray-50 hover:bg-gray-100 text-gray-700 font-medium rounded-xl text-sm border"
              style={{ borderColor: '#e8e6e2' }}
            >
              Back to Login
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
